import React from 'react';
import { Box, Typography, Paper, keyframes } from "@mui/material";
import { Handle, Position, NodeProps } from "reactflow";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import BuildIcon from "@mui/icons-material/Build";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import LoopIcon from "@mui/icons-material/Loop";
import MergeIcon from "@mui/icons-material/CallMerge";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { StepType } from "./WorkflowTypes";

const pulseAnimation = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

export interface WorkflowNodeData {
  label: string;
  status?: "ready" | "completed" | "failed" | "running" | "paused" | "skipped";
  type: StepType | "trigger";
  show_status?: boolean;
  config: {
    id: string;
    version: string;
    triggerType?: "schedule" | "webhook";
    cron?: string;
    webhookUrl?: string;
    webhookConfig?: {
      adapter_type: string;
      secrets: Record<string, string>;
    };
    prompt?: string;
    depends_on?: string[];
    input_mapping?: Record<string, string>;
    // Conditional
    routing_config?: any;
    // Human input
    title?: string;
    data_to_display?: Record<string, string>;
    input_fields?: any[];
    timeout_minutes?: number;
    on_timeout?: string;
    // Loop
    loop_config?: any;
    loop_body_steps?: any[];
    output_mode?: string;
    // Merge
    merge_strategy?: string;
    // Wait
    wait_config?: any;
    // Common step fields
    timeout_seconds?: number;
    on_failure?: string;
  };
  rawData?: Record<string, any>;
}

const hexToRgba = (hex:any, alpha:any) => {
  let r = parseInt(hex.slice(1, 3), 16),
      g = parseInt(hex.slice(3, 5), 16),
      b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const WorkflowNode = ({ data, selected }: NodeProps<WorkflowNodeData>) => {
  const isAgent = data.type === "agent";
  const isTrigger = data.type === "trigger";
  const isTool = data.type === "tool";
  const isConditional = data.type === "conditional";
  const isHumanInput = data.type === "human_input";
  const isLoop = data.type === "loop";
  const isMerge = data.type === "merge";
  const isWait = data.type === "wait";

  const getThemeColor = () => {
    if (isAgent) return "#B87333";
    if (isTool) return "#789769";
    if (isConditional) return "#D4A017";
    if (isHumanInput) return "#8E588E";
    if (isLoop) return "#4A90D9";
    if (isMerge) return "#2E8B57";
    if (isWait) return "#6B7B8D";
    if (isTrigger && data.config.triggerType === "schedule") return "#5A7D9A";
    if (isTrigger) return "#2C3E50";
    return "#64748B";
  };

  const themeColor = getThemeColor();
  const tabBg = "#f1f5f9";

  const getTypeLabel = () => {
    if (isTrigger) return data.config.triggerType || "Trigger";
    if (isHumanInput) return "approval";
    if (isConditional) return "conditional";
    if (isLoop) return "loop";
    if (isMerge) return "merge";
    if (isWait) return "wait";
    return data.type;
  };

  const getNodeIcon = () => {
    if (isTrigger) {
      return data.config.triggerType === 'schedule' ? <ScheduleIcon sx={{ fontSize: 18 }}/> : <CloudQueueIcon sx={{ fontSize: 18 }}/>;
    }
    if (isAgent) return <SmartToyIcon sx={{ fontSize: 18 }} />;
    if (isTool) return <BuildIcon sx={{ fontSize: 16 }} />;
    if (isConditional) return <AltRouteIcon sx={{ fontSize: 18 }} />;
    if (isHumanInput) return <PersonOutlineIcon sx={{ fontSize: 18 }} />;
    if (isLoop) return <LoopIcon sx={{ fontSize: 18 }} />;
    if (isMerge) return <MergeIcon sx={{ fontSize: 18 }} />;
    if (isWait) return <HourglassEmptyIcon sx={{ fontSize: 18 }} />;
    return <BuildIcon sx={{ fontSize: 16 }} />;
  };

  const shouldShowChip = data.show_status !== undefined ? data.show_status : false;

  const getStatusColor = () => {
    if (data.status === "completed") return "#22c55e";
    if (data.status === "failed") return "#ef4444";
    if (data.status === "running") return "#3B82F6";
    if (data.status === "paused") return "#F59E0B";
    if (data.status === "skipped") return "#94A3B8";
    if (data.status === "ready") return "#94A3B8";
    return "#22c55e";
  };

  const statusColor = getStatusColor();
  const isSkipped = data.status === "skipped";
  const isRunning = data.status === "running";

  const getStatusBgColor = () => {
    if (data.status === "completed") return "#f0fdf4";
    if (data.status === "failed") return "#fef2f2";
    if (data.status === "running") return "#eff6ff";
    if (data.status === "paused") return "#fffbeb";
    return "#f8fafc"; // skipped, queued
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minWidth: 160,
      maxWidth: 250,
      ...(isSkipped && { opacity: 0.45 }),
      ...(isRunning && { animation: `${pulseAnimation} 1.5s ease-in-out infinite` }),
    }}>

      {/* --- CONDITIONAL CHIP --- */}
      {shouldShowChip && (
        <Box sx={{
            position: "absolute",
            top: -12,
            right: 0,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            bgcolor: getStatusBgColor(),
            color: statusColor,
            px: 1,
            py: 0.4,
            borderRadius: "18px",
            border: `1px solid ${statusColor}44`,
            zIndex: 3,
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <Typography sx={{ fontSize: "9px", fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span style={{ fontSize: '10px' }}>
              {data.status === "paused" ? "\u23F8"
                : data.status === "skipped" ? "\u2298"
                : data.status === "running" ? "\u27F3"
                : data.status === "failed" ? "\u2717"
                : "\u2713"}
            </span> {data.status}
          </Typography>
        </Box>
      )}
      {/* 1. HEADER TAB */}
      <Box sx={{
          alignSelf: 'flex-start',
          display: "flex",
          alignItems: "center",
          gap: 0.6,
          px: 1,
          py: 0.3,
          bgcolor: tabBg,
          borderRadius: "6px 6px 0 0",
          border: selected ? "2px solid" : "1.5px solid",
          borderColor: selected ? themeColor : hexToRgba(themeColor, 0.45),
          borderBottom: "none",
          position: 'relative',
          zIndex: 2,
      }}>
        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: themeColor }} />
        <Typography sx={{ fontWeight: 800, color: "#475569", textTransform: "uppercase", fontSize: "8px", letterSpacing: '0.03em' }}>
          {getTypeLabel()}
        </Typography>
        {isTrigger && (
          <BatteryChargingFullIcon sx={{ fontSize: 12 }} />
        )}
      </Box>

      {/* 2. MAIN NODE BODY */}
      <Paper
        elevation={0}
        sx={{
          marginTop: "-1.5px",
          borderRadius: "0 8px 8px 8px",
          border: `2px solid ${selected ? themeColor : themeColor + '72'}`,
          bgcolor: "white",
          p: 1,
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          position: "relative",
          zIndex: 1,
          transition: "all 0.15s ease-in-out",
          ...(selected && {
            boxShadow: `0 0 15px ${themeColor}22`,
          })
      }}>
        {/* TARGET HANDLE (Input) - Left */}
        {!isTrigger && (
          <Handle
            type="target"
            position={Position.Left}
            style={{
              background: "#94a3b8",
              width: 8,
              height: 8,
              border: '1.5px solid white',
              left: -5, // Aligned to left edge
              zIndex: 10
            }}
          />
        )}

        <Box sx={{
          width: 32, height: 32, borderRadius: "8px",
          bgcolor: themeColor, display: "flex",
          alignItems: "center", justifyContent: "center",
          color: "#FFFFFF", flexShrink: 0
        }}>
           {getNodeIcon()}
        </Box>

        <Typography sx={{
          fontWeight: 700,
          fontSize: '11px',
          color: "#334155",
          flexGrow: 1,
          wordBreak: "break-word",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          lineHeight: 1.2 }}
        >
          {data.label}
        </Typography>

        {/* SOURCE HANDLE (Output) - Right */}
        <Handle
          type="source"
          position={Position.Right}
          style={{
            background: "white",
            border: `1.5px solid ${themeColor}`,
            width: 8,
            height: 8,
            right: -5, // Aligned to right edge
            zIndex: 10
          }}
        />
      </Paper>
    </Box>
  );
};
export default WorkflowNode;
